import ComponentBase from 'components/_base/ComponentBase';
import jQuery from 'jquery';
import 'jquery-ui/ui/widgets/autocomplete';
import _ from 'underscore';
import GoogleMapsUtils from 'helpers/GoogleMapsUtils';

/**
 * @namespace core.components.autocomplete
 */

const Autocomplete = ComponentBase.extend(
  /**
   * @lends core.components.autocomplete.Autocomplete.prototype
   * @fires Widget#USER_ACTION_TRACKING
   */
  {
    componentClass: 'autocomplete',
    template: require('./autocomplete.hbs'),

    /**
     * The providers used by the autocomplete
     * @protected
     * @type {core.components.autocomplete.providers.AutocompleteProviderBase[]}
     */
    providers: [],

    /**
     * Initialize the component.
     * @class Autocomplete
     * @classdesc
     * @extends core.components.ComponentBase
     * @memberof core.components.autocomplete
     * @param {Object} options - The config provided by json
     * @example <caption>Autocomplete options</caption>
     * "order": 1,
     * "componentConstructorClass": "Autocomplete",
     * "position": "header",
     * "properties": {
     *         "categorized": false,
     *         "providers": {
     *           "GoogleAutocomplete": {
     *             "icons": ["transit_station", "bus_station", "train_station"],
     *             "minLength": 3,
     *              "maxResults": 5,
     *              "serviceOptions": {
     *               "componentRestrictions": {
     *                 "ref:country:countryRestrictionAutocomplete": "gb"
     *               }
     *             }
     *           }
     *         },
     *         "ref:closeOtherPanels:autocompleteCloseOtherPanels": true,
     *         "ref:selectedMode:autocompleteSelectedMode": "both",
     *         "ref:zoomLevel:autocompleteZoom": 15
     * }
     * @listens {@link Widget#CREATE_POINT_REFERENCE}
     * @listens {@link Widget#CHANGE_POINT_TO_AUTOCOMPLETE}
     */
    initialize(options) {
      this.templateConfig.placeholderText = options.properties.placeholderText || this.widget.polyglot.t(
        'autocomplete.placeholder');
      ComponentBase.prototype.initialize.call(this, options);
      this.prepareConf();
      this.categorized = (_.isUndefined(this.properties.categorized) || this.properties.categorized) ? true : false;
      this.autocompleteType = (this.categorized) ? 'catcomplete' : 'autocomplete';
      this.input = this.$el.find('input');
      this.promisesFinished = 0;
      this.zoomTarget = options.properties.zoomTarget || 13;
      this._initProviders();
      this.autoFocus = options.autoFocus === undefined ? true : false;
      if (this.categorized) {
        this._prepareCategoryAutocomplete();
      } else {
        this._prepareAutocomplete();
      }
      this.resultsPanel = options.resultsPanel || this.$el.find('.results');
      this.errorPanel = options.errorPanel || this.$el.find('.error-search-panel');
      this._initAutoComplete();
      if (this.overridable) {
        this.listenTo(this.bus, 'CREATE_POINT_REFERENCE', (configPRef) => {
          this._manageEventCreatePOI(configPRef);
        });
      }
      this.listenTo(this.bus, 'CHANGE_POINT_TO_AUTOCOMPLETE', () => {
        this._checkInputEmpty();
      });
    },

    _manageEventCreatePOI(configPRef) {
      if (configPRef.sender !== this) {
        this._resetAutocomplete();
      }
    },

    events: {
      'click .reset': '_resetAutocomplete',
      'keyup input': '_checkInputEmpty'
    },

    _initProviders() {
      let _providers = this.properties.providers;
      if (_providers) {
        _.each(_providers, (conf, name) => {
          this.providers.push(new Autocomplete.providerClasses[name](conf, this.widget));
        });
      }
    },

    _checkInputEmpty() {
      if (!this.input.val()) {
        this._hideResetIcon();
        this.errorPanel.hide();
        this._userActionReset();
      } else {
        this._showResetIcon();
      }
    },

    _getMinKeystroke() {
      let min = null;
      _.each(this.providers, function(provider) {
        if (_.isObject(provider._sources)) {
          _.each(provider._sources, function(source) {
            if (min === null || min > source.minLength) {
              min = source.minLength;
            }
          });
        } else {
          if (min === null || min > provider.conf.minLength) {
            min = provider.conf.minLength;
          }
        }
      });
      return min;
    },

    _initAutoComplete() {
      this.input[this.autocompleteType]({
        minLength: this._getMinKeystroke(),
        source: (request, response) => {
          this._lookup(request, response);
        },
        appendTo: this.resultsPanel,
        autoFocus: this.autoFocus,
        select: (event, ui) => {
          this._onSelect(ui.item);
        },
        open: (event, ui) => {
          if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
            jQuery('.ui-autocomplete').off('menufocus hover mouseover');
          }
          this.onOpen();
        },
        close: () => {
          this.onClose();
        }
      });
      this._calculateMaxHeightAutocomplete();
    },
    onOpen() {},
    onClose() {},
    _calculateMaxHeightAutocomplete() {
      //calculate max height in order to autocomplete doesnt overlap the Google logo which is 90px height
      let height = (this.widget.$el.find('.lmm-maps-widget-container').height() - 90) + 'px';
      this.resultsPanel.find('ul').css('max-height', height);
    },
    _lookup(request, response) {
      let promises = [];
      let _this = this;
      this.promisesFinished = 0;

      this.widget.trackUserAction({
        tracking: 'Autocomplete',
        text: request.term
      });

      this.providers.forEach((provider) => {
        provider.requestIsFinished = false;
        //This condition is true by default, but it could be that in a specific case we don't want to use the provider
        if (provider.validate(request.term)) {
          promises.push(provider.getPredictions(request.term));
        }
      });

      //If we have two or more providers and a provider finishes first
      //then we show results and loading icon in unfinished providers
      _.each(promises, (promise) => {
        promise.then((resultData) => {
          this.promisesFinished++;
          if (this.promisesFinished < promises.length) {
            //If only this promise is finished we show results
            let totalResults = [];
            totalResults.push.apply(totalResults, resultData.filter((result) => {
              return !_.isUndefined(result.code);
            }));
            if (totalResults.length > 0) {
              this.errorPanel.hide();
              response(totalResults);
            } else {
              //Response with an empty item to trigger _renderMenu
              response([{}]);
            }
          }
        });
      });

      jQuery.when.apply(jQuery, promises).then(function() {
        let totalResults = [],
          args = arguments || [],
          hasResults = false;

        function filterResults(results, totalResults) {
          totalResults.push.apply(totalResults, results.filter((result) => {
            return !_.isUndefined(result.code);
          }));
        }

        for (let i = 0; i < args.length; i++) {
          let results = args[i];
          filterResults(results, totalResults);
        }

        if (totalResults.length > 0) {
          if (totalResults.length === 1 && _.isEmpty(totalResults[0])) {
            hasResults = false;
          } else {
            hasResults = true;
          }
        } else {
          hasResults = false;
        }

        if (!hasResults) {
          _this.errorPanel.show();
        } else {
          _this.errorPanel.hide();
        }

        response(totalResults);
      }, function(e) {
        response([]);
      });
    },

    _reloadAutocompleteInput() {
      let inputValue = this.input.val();
      this.input.val('');
      this._userActionReset();
      this.input.val(inputValue);
    },

    _onSelect(item) {
      this._reloadAutocompleteInput();
      item.coordinates().done((coords) => {
        let location = null;
        if (coords instanceof google.maps.LatLng) {
          location = coords;
        } else if (!_.isUndefined(coords.viewport)) {
          location = coords.location;
        }
        if (this._canMoveMap(location)) {
          this._goToLocation(coords);
          this._createPReference(location, item);
          this._showResetIcon();
          this.input.blur();
          this.input.trigger('blur');
          this.widget.trackUserAction({
            tracking: 'Autocomplete',
            selected: item.value
          });
          /**
           * Event triggered when a result of autcomplete is selected
           * @event Widget#AUTOCOMPLETE_RESULT_SELECTED
           * @param {Autocomplete} autocompleteComponent - The instace of autocomplete
           */
          this.bus.trigger('AUTOCOMPLETE_RESULT_SELECTED', item, this);
        } else {
          this._resetAutocomplete();
          this.widget.messageControl.createMessage(
            'error', this.widget.polyglot.t('panelTT.outsideBounds'), false, false);
        }
      });
    },

    _canMoveMap(location) {
      return (!this.widget.config.defaultView.limitBounds ||
        (this.widget.config.defaultView.limitBounds &&
          GoogleMapsUtils.containPointInBBox(GoogleMapsUtils.getBounds(this.widget.config.defaultView.bounds),
            location)));
    },

    _resetAutocomplete() {
      this._hideResetIcon();
      this.errorPanel.hide();
      this.input.val('');
      this._userActionReset();
    },

    _showResetIcon() {
      this.$el.find('.reset').show();
    },

    _hideResetIcon() {
      this.$el.find('.reset').hide();
    },
    _userActionReset() {
      /**
       * Event triggered when the autocomplete content is deleted
       * @event Widget#RESET_AUTOCOMPLETE
       * @param {core.component.autocomplete.Autocomplete} autocompleteComponent - The instace of autocomplete
       */
      this.bus.trigger('RESET_AUTOCOMPLETE', this);
    },

    _goToLocation(coords) {
      if (coords instanceof google.maps.LatLng) {
        this.map.panTo(coords);
        this.map.setZoom(this.zoomTarget);
      } else if (!_.isUndefined(coords.viewport)) {
        this.map.panTo(coords.location);
        this.map.fitBounds(coords.viewport);
      }
    },

    _createPReference(pos, item) {
      let bus = this.bus;
      let config = {
        position: pos,
        sender: this,
        properties: item
      };
      bus.trigger('CREATE_POINT_REFERENCE', config);
    },

    _prepareCategoryAutocomplete() {
      jQuery.widget('custom.catcomplete', jQuery.ui.autocomplete, {
        _create: function() {
          this._super();
          this.widget().menu('option', 'items', '> :not(.ui-autocomplete-category)');
        },
        _renderItem: function(ul, item) {
          let icon = _.isUndefined(item.icon) ? false : item.icon;
          let li = jQuery('<li></li>');
          if (icon) {
            li.append(`<i class="autocomplete-ui-icon ${icon}"></i> <span>${item.label}</span>`);
          } else {
            li.append(`<span>${item.label}</span>`);
          }
          return li
            .appendTo(ul);
        },
        _renderMenu: function(ul, items) {
          var _this = this,
            currentCategory = '';

          //Loading icon if provider request is not finished
          _.each(_this.providers, function(provider) {
            if (_this.promisesFinished < _this.providers.length && !provider.requestIsFinished) {
              ul.append('<li class="ui-autocomplete-category"><span>' + provider.conf.category +
                '</span><div class="loading-data categorized"></div></li>');
            }
          });

          jQuery.each(items, function(index, item) {
            var li;
            if (item.category) {
              if (item.category !== currentCategory) {
                ul.append('<li class="ui-autocomplete-category">' + item.category + '</li>');
                currentCategory = item.category;
              }
              li = _this._renderItemData(ul, item);
              if (item.category) {
                li.attr('aria-label', item.category + ' : ' + item.label);
              }
            } else {
              _this._renderItemData(ul, item);
            }
          });
        }
      });
    },
    _prepareAutocomplete() {
      jQuery.widget('custom.autocomplete', jQuery.ui.autocomplete, {
        _create: function() {
          this._super();
          this.widget().menu('option', 'items', '> :not(.ui-autocomplete-loading)');
        },
        _renderMenu: function(ul, items) {
          var _this = this,
            renderLoading = false;
          //Loading icon if provider request is not finished
          _.each(_this.providers, function(provider) {
            if (_this.promisesFinished < _this.providers.length && !provider.requestIsFinished) {
              renderLoading = true;
            }
          });
          if (renderLoading) {
            ul.append('<li class="ui-autocomplete-loading"><div class="loading-data uncategorized"></div></li>');
          }
          jQuery.each(items, function(index, item) {
            _this._renderItemData(ul, item);
          });
        },
        _renderItem: function(ul, item) {
          let icon = _.isUndefined(item.icon) ? false : item.icon;
          let li = jQuery(`<li class="${item.type}"></li>`);
          let content = item.content ? item.content : item.label ? `<span>${item.label}</span>` : '';
          if (icon) {
            li.append(`<i class="autocomplete-ui-icon ${icon}"></i>${content} `);
          } else {
            li.append(content);
          }
          return li
            .appendTo(ul);
        }
      });
    },
    prepareConf() {
      if (this.properties.icons === false) {
        _.each(this.properties.providers, (provider) => { provider.icons = false; });
      }
    }
  });

/**
 * The provider classes available to Autocomplete instances.
 * @name providerClasses
 * @type {Object<string, function>}
 * @static
 * @protected
 * @memberof core.components.autocomplete.Autocomplete
 */
Autocomplete.providerClasses = {};

/**
 * Registers a provider class so it can be instantiated from configuration. This method must be called
 * when registering the Autocomplete class as component to be used in the Widget's config in the Widget
 * extending class registerConfigClasses method.
 * @name registerProviderClass
 * @function
 * @static
 * @param {string} providerName the name of the registered provider class as will appear in the configuration.
 * @param {function} providerConstructor the class (i.e. its JS constructor) that will be created 
 * when the provierName appears in the config.
 * @memberof core.components.autocomplete.Autocomplete
 * @example <caption>Method use</caption>
 * export default Widget.extend({
 *   ...
 *   registerConfigClasses(registerComponent, registerLayer, registerLayerProvider, registerService) {
 *     ...
 *     AutocompleteComponent.registerProvider('GPlacesAutocomplete', GooglePlacesAutocomplete);
 *     registerComponent('Autocomplete', AutocompleteComponent);
 *     ...
 *   },
 *    ...
 * });  
 */
Autocomplete.registerProviderClass = function(providerName, providerConstructor) {
  Autocomplete.providerClasses[providerName] = providerConstructor;
};

export default Autocomplete;



// WEBPACK FOOTER //
// ./lmm-widget/src/components/autocomplete/Autocomplete.js